import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ArrowLeft, Truck, Calendar, User, Thermometer, PackageCheck, Camera,
  Plus, Minus, Trash2, Loader2, CheckCircle2, XCircle, PhoneCall, ClipboardCheck
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';

/**
 * DeliveryReceptionPage — Contrôle à réception PLEINE PAGE
 * Objectif : pointage en moins de 2 minutes.
 * Tout est conforme par défaut, on ne saisit que les exceptions.
 */

// Compression photo côté client (max 1280px, JPEG 70%)
const compressImage = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    const maxDim = 1280;
    let { width, height } = img;
    if (width > maxDim || height > maxDim) {
      const ratio = Math.min(maxDim / width, maxDim / height);
      width = Math.round(width * ratio);
      height = Math.round(height * ratio);
    }
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d').drawImage(img, 0, 0, width, height);
    URL.revokeObjectURL(url);
    resolve(canvas.toDataURL('image/jpeg', 0.7));
  };
  img.onerror = reject;
  img.src = url;
});

const GlobalCheck = ({ icon: Icon, label, ok, onChange, testId }) => (
  <button
    type="button"
    onClick={() => onChange(!ok)}
    className={`flex-1 flex items-center gap-3 p-4 rounded-xl border-2 transition-colors text-left ${
      ok ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-400'
    }`}
    data-testid={testId}
  >
    <Icon className={`w-7 h-7 shrink-0 ${ok ? 'text-green-600' : 'text-red-600'}`} />
    <div>
      <p className="font-semibold text-gray-900">{label}</p>
      <p className={`text-sm font-medium flex items-center gap-1 ${ok ? 'text-green-700' : 'text-red-700'}`}>
        {ok ? <><CheckCircle2 className="w-4 h-4" /> Conforme</> : <><XCircle className="w-4 h-4" /> Non conforme</>}
      </p>
    </div>
  </button>
);

const DeliveryReceptionPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const photoInputRef = useRef(null);

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lines, setLines] = useState([]);
  const [dlcFlags, setDlcFlags] = useState({});
  const [supplierProducts, setSupplierProducts] = useState([]);
  const [temperatureOk, setTemperatureOk] = useState(true);
  const [packagingOk, setPackagingOk] = useState(true);
  const [notes, setNotes] = useState('');
  const [photos, setPhotos] = useState([]);
  const [horsCommande, setHorsCommande] = useState([]);
  const [hcProductId, setHcProductId] = useState('');
  const [hcQty, setHcQty] = useState('1');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get(`/orders/${orderId}`);
        if (data.status !== 'sent') {
          toast.error('Cette livraison a déjà été pointée');
          navigate('/deliveries');
          return;
        }
        setOrder(data);

        const initial = data.items.map(item => ({
          product_id: item.product_id,
          code: item.code,
          article: item.article,
          qty_ordered: parseFloat(item.quantity) || 0,
          qty_received: parseFloat(item.quantity) || 0,
          status: 'conforme',
          dlc: '',
          note: '',
        }));
        (data.custom_products || []).forEach((cp, idx) => {
          initial.push({
            product_id: `custom_${idx}`,
            code: '',
            article: `${cp.name} (hors catalogue)`,
            qty_ordered: parseFloat(cp.quantity) || 0,
            qty_received: parseFloat(cp.quantity) || 0,
            status: 'conforme',
            dlc: '',
            note: '',
          });
        });
        setLines(initial);

        // Produits du fournisseur : flags DLC + picker hors commande
        const prodRes = await api.get(`/products?supplier_id=${data.supplier_id}`);
        const flags = {};
        prodRes.data.forEach(p => { flags[p.id] = Boolean(p.dlc); });
        setDlcFlags(flags);
        setSupplierProducts(prodRes.data);
      } catch (e) {
        toast.error('Erreur lors du chargement de la livraison');
        navigate('/deliveries');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateLine = (productId, patch) => {
    setLines(prev => prev.map(l => {
      if (l.product_id !== productId) return l;
      const updated = { ...l, ...patch };
      // Statut cohérent avec la quantité
      if (patch.qty_received !== undefined) {
        const q = parseFloat(patch.qty_received);
        if (isNaN(q) || q === updated.qty_ordered) updated.status = 'conforme';
        else if (q < updated.qty_ordered && !['reliquat', 'annule'].includes(updated.status)) updated.status = 'reliquat';
        else if (q > updated.qty_ordered && !['surplus_accepte', 'surplus_refuse'].includes(updated.status)) updated.status = 'surplus_accepte';
      }
      return updated;
    }));
  };

  const handlePhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (photos.length + files.length > 5) {
      toast.error('Maximum 5 photos');
      return;
    }
    try {
      const compressed = await Promise.all(files.map(compressImage));
      setPhotos(prev => [...prev, ...compressed]);
    } catch {
      toast.error('Erreur lors du traitement de la photo');
    }
    e.target.value = '';
  };

  const addHorsCommande = () => {
    if (!hcProductId) { toast.error('Sélectionnez un produit'); return; }
    const qty = parseFloat(hcQty);
    if (!qty || qty <= 0) { toast.error('Quantité invalide'); return; }
    const product = supplierProducts.find(p => p.id === hcProductId);
    if (horsCommande.some(h => h.product_id === hcProductId)) {
      toast.error('Produit déjà ajouté');
      return;
    }
    setHorsCommande(prev => [...prev, {
      product_id: hcProductId,
      code: product?.code || '',
      article: product?.article || '',
      quantity: qty,
      dlc: '',
      dlcFlag: Boolean(product?.dlc),
    }]);
    setHcProductId('');
    setHcQty('1');
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await api.put(`/orders/${orderId}/receive`, {
        items: lines.map(l => ({
          product_id: l.product_id,
          quantity_received: parseFloat(l.qty_received) || 0,
          line_status: l.status,
          dlc: l.dlc || null,
          note: l.note || null,
        })),
        hors_commande: horsCommande.map(h => ({
          product_id: h.product_id,
          quantity: h.quantity,
          dlc: h.dlc || null,
        })),
        temperature_ok: temperatureOk,
        packaging_ok: packagingOk,
        notes,
        photos,
      });
      toast.success('✅ Réception validée');
      navigate(`/deliveries/${orderId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la validation');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B4332]"></div>
      </div>
    );
  }
  if (!order) return null;

  const nbExceptions = lines.filter(l => l.status !== 'conforme').length + horsCommande.length;

  return (
    <div className="space-y-5 animate-slide-in pb-28" data-testid="delivery-reception-page">
      {/* Header */}
      <div>
        <Button variant="ghost" onClick={() => navigate('/deliveries')} className="mb-2 -ml-2 text-gray-600">
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour aux livraisons
        </Button>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Pointage {order.order_number}</h1>
          <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100">À réceptionner</Badge>
        </div>
        <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-600">
          <span className="flex items-center gap-1"><Truck className="w-4 h-4" /> {order.supplier_name}</span>
          <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> Prévue le {format(new Date(order.delivery_date), 'd MMMM', { locale: fr })}</span>
          <span className="flex items-center gap-1"><User className="w-4 h-4" /> {user?.first_name} {user?.last_name} • {format(new Date(), "d MMM HH:mm", { locale: fr })}</span>
        </div>
      </div>

      {/* Contrôles globaux */}
      <div className="flex flex-col sm:flex-row gap-3">
        <GlobalCheck icon={Thermometer} label="Température des denrées" ok={temperatureOk} onChange={setTemperatureOk} testId="temperature-check" />
        <GlobalCheck icon={PackageCheck} label="Intégrité du conditionnement" ok={packagingOk} onChange={setPackagingOk} testId="packaging-check" />
      </div>

      {/* Lignes produits */}
      <Card className="border border-gray-200">
        <CardContent className="p-0 divide-y">
          {lines.map((line) => {
            const isConforme = line.status === 'conforme';
            const isLess = parseFloat(line.qty_received) < line.qty_ordered;
            const isMore = parseFloat(line.qty_received) > line.qty_ordered;
            const hasDlcFlag = dlcFlags[line.product_id];
            return (
              <div
                key={line.product_id}
                className={`p-3 sm:p-4 ${isConforme ? '' : 'bg-orange-50/60'}`}
                data-testid={`reception-line-${line.code || line.product_id}`}
              >
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-[200px]">
                    {line.code && (
                      <span className="text-xs font-mono text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{line.code}</span>
                    )}
                    <p className="font-medium text-gray-900">{line.article}</p>
                    <p className="text-xs text-gray-500">Commandé : <strong>{line.qty_ordered}</strong></p>
                  </div>

                  {/* Quantité reçue */}
                  <div className="flex items-center gap-1">
                    <Button
                      type="button" size="icon" variant="outline" className="h-9 w-9"
                      onClick={() => updateLine(line.product_id, { qty_received: Math.max(0, (parseFloat(line.qty_received) || 0) - 1) })}
                      data-testid={`qty-minus-${line.code || line.product_id}`}
                    ><Minus className="w-4 h-4" /></Button>
                    <Input
                      type="text" inputMode="decimal"
                      value={line.qty_received}
                      onChange={(e) => updateLine(line.product_id, { qty_received: e.target.value.replace(',', '.') })}
                      className={`h-9 w-16 text-center font-semibold ${isConforme ? '' : 'border-orange-400'}`}
                      data-testid={`qty-input-${line.code || line.product_id}`}
                    />
                    <Button
                      type="button" size="icon" variant="outline" className="h-9 w-9"
                      onClick={() => updateLine(line.product_id, { qty_received: (parseFloat(line.qty_received) || 0) + 1 })}
                      data-testid={`qty-plus-${line.code || line.product_id}`}
                    ><Plus className="w-4 h-4" /></Button>
                  </div>

                  {/* Statut */}
                  <div className="min-w-[170px]">
                    {isConforme && (
                      <Badge className="bg-green-100 text-green-700 border-green-300 hover:bg-green-100">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Conforme
                      </Badge>
                    )}
                    {isLess && (
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => updateLine(line.product_id, { status: 'reliquat' })}
                          className={`px-2 py-1 rounded text-xs font-medium border ${line.status === 'reliquat' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-700 border-amber-300'}`}
                          data-testid={`status-reliquat-${line.code || line.product_id}`}
                        >⏳ Reliquat</button>
                        <button
                          type="button"
                          onClick={() => updateLine(line.product_id, { status: 'annule' })}
                          className={`px-2 py-1 rounded text-xs font-medium border ${line.status === 'annule' ? 'bg-red-500 text-white border-red-500' : 'bg-white text-red-700 border-red-300'}`}
                          data-testid={`status-annule-${line.code || line.product_id}`}
                        >❌ Annulé</button>
                      </div>
                    )}
                    {isMore && (
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => updateLine(line.product_id, { status: 'surplus_accepte' })}
                          className={`px-2 py-1 rounded text-xs font-medium border ${line.status === 'surplus_accepte' ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-blue-700 border-blue-300'}`}
                          data-testid={`status-accepte-${line.code || line.product_id}`}
                        >➕ Accepté</button>
                        <button
                          type="button"
                          onClick={() => updateLine(line.product_id, { status: 'surplus_refuse' })}
                          className={`px-2 py-1 rounded text-xs font-medium border ${line.status === 'surplus_refuse' ? 'bg-red-500 text-white border-red-500' : 'bg-white text-red-700 border-red-300'}`}
                          data-testid={`status-refuse-${line.code || line.product_id}`}
                        >↩️ Refusé</button>
                      </div>
                    )}
                  </div>

                  {/* DLC pour produits flagués */}
                  {hasDlcFlag && (
                    <div className="flex items-center gap-1">
                      <Label className="text-xs text-amber-700 font-semibold">DLC</Label>
                      <Input
                        type="date"
                        value={line.dlc}
                        onChange={(e) => updateLine(line.product_id, { dlc: e.target.value })}
                        className="h-9 w-36 text-sm border-amber-300"
                        data-testid={`dlc-input-${line.code || line.product_id}`}
                      />
                    </div>
                  )}
                </div>
                {/* Explication du statut choisi */}
                {line.status === 'reliquat' && (
                  <p className="text-xs text-amber-700 mt-1">Le fournisseur nous le doit — suggéré à la prochaine commande</p>
                )}
                {line.status === 'annule' && (
                  <p className="text-xs text-red-700 mt-1">Sort définitivement de la commande — ne doit pas être facturé</p>
                )}
                {line.status === 'surplus_refuse' && (
                  <p className="text-xs text-red-700 mt-1">Retourné au livreur — avoir attendu s'il est facturé</p>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Hors commande */}
      <Card className="border border-dashed border-blue-300 bg-blue-50/40">
        <CardContent className="p-4 space-y-3">
          <p className="font-semibold text-gray-900 flex items-center gap-2">
            <PhoneCall className="w-4 h-4 text-blue-600" /> Article reçu hors commande
            <span className="text-xs text-gray-500 font-normal">(ajout passé par téléphone)</span>
          </p>
          {horsCommande.map((h, idx) => (
            <div key={h.product_id} className="flex flex-wrap items-center gap-2 p-2 bg-white rounded-lg border" data-testid={`hc-line-${h.code}`}>
              <Badge className="bg-blue-100 text-blue-700 border-blue-300 hover:bg-blue-100">Hors commande</Badge>
              <span className="text-sm font-medium flex-1">{h.code} — {h.article}</span>
              <span className="text-sm">Qté : <strong>{h.quantity}</strong></span>
              {h.dlcFlag && (
                <Input
                  type="date" value={h.dlc}
                  onChange={(e) => setHorsCommande(prev => prev.map((x, i) => i === idx ? { ...x, dlc: e.target.value } : x))}
                  className="h-8 w-36 text-sm border-amber-300"
                />
              )}
              <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => setHorsCommande(prev => prev.filter((_, i) => i !== idx))}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex-1 min-w-[220px]">
              <Select value={hcProductId} onValueChange={setHcProductId}>
                <SelectTrigger className="bg-white" data-testid="hc-product-select">
                  <SelectValue placeholder="Choisir dans le catalogue du fournisseur..." />
                </SelectTrigger>
                <SelectContent>
                  {supplierProducts
                    .filter(p => !order.items.some(i => i.product_id === p.id))
                    .map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.code} — {p.article}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <Input
              type="text" inputMode="decimal" value={hcQty}
              onChange={(e) => setHcQty(e.target.value.replace(',', '.'))}
              className="h-10 w-20 text-center bg-white" placeholder="Qté"
              data-testid="hc-qty-input"
            />
            <Button type="button" variant="outline" onClick={addHorsCommande} className="border-blue-400 text-blue-700" data-testid="hc-add-button">
              <Plus className="w-4 h-4 mr-1" /> Ajouter
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Photos + Commentaire */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="border border-gray-200">
          <CardContent className="p-4 space-y-3">
            <p className="font-semibold text-gray-900 flex items-center gap-2">
              <Camera className="w-4 h-4 text-gray-600" /> Photos <span className="text-xs text-gray-500 font-normal">(colis abîmé, BL... max 5)</span>
            </p>
            <div className="flex flex-wrap gap-2">
              {photos.map((p, idx) => (
                <div key={idx} className="relative">
                  <img src={p} alt={`photo ${idx + 1}`} className="w-20 h-20 object-cover rounded-lg border" />
                  <button
                    type="button"
                    onClick={() => setPhotos(prev => prev.filter((_, i) => i !== idx))}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center"
                  >×</button>
                </div>
              ))}
              {photos.length < 5 && (
                <button
                  type="button"
                  onClick={() => photoInputRef.current?.click()}
                  className="w-20 h-20 border-2 border-dashed rounded-lg flex items-center justify-center text-gray-400 hover:border-gray-500 hover:text-gray-600"
                  data-testid="add-photo-button"
                >
                  <Camera className="w-6 h-6" />
                </button>
              )}
              <input ref={photoInputRef} type="file" accept="image/*" multiple capture="environment" className="hidden" onChange={handlePhotos} data-testid="photo-input" />
            </div>
          </CardContent>
        </Card>
        <Card className="border border-gray-200">
          <CardContent className="p-4 space-y-2">
            <p className="font-semibold text-gray-900">Commentaire</p>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Remarques libres sur la livraison..."
              rows={3}
              data-testid="reception-notes"
            />
          </CardContent>
        </Card>
      </div>

      {/* Footer sticky : validation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-3 sm:p-4 z-40">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-4">
          <div className="text-sm">
            {nbExceptions === 0 && temperatureOk && packagingOk ? (
              <span className="text-green-700 font-medium flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" /> Tout est conforme
              </span>
            ) : (
              <span className="text-orange-700 font-medium">
                {nbExceptions} exception(s){!temperatureOk || !packagingOk ? ' + contrôle global non conforme' : ''}
              </span>
            )}
          </div>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-[#1B4332] hover:bg-[#0D2E1F] text-white px-8 h-12 text-base"
            data-testid="validate-reception-button"
          >
            {submitting ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Validation...</>
            ) : (
              <><ClipboardCheck className="w-5 h-5 mr-2" /> Valider la réception</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DeliveryReceptionPage;
